import React, { useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import axios from 'axios';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

export default function PermissionTypeModel({ open, onClose, permissionTypeId }) {
    const apiUrl = process.env.REACT_APP_API_BASE_URL;
    const bearerToken = localStorage.getItem('token');
    const [isLoading, setIsLoading] = useState(false);
    const [isEditLoading, setIsEditLoading] = useState(false);
    const modalRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (modalRef.current && !modalRef.current.contains(event.target)) {
                onClose();
            }
        };

        if (open) {
            document.addEventListener("mousedown", handleClickOutside);
        }

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [open, onClose]);

    const handleClose = () => {
        onClose();
        newPermissionType.resetForm({ values: { name: '' } });
    };

    useEffect(() => {
        const fetchPermissionType = async () => {
            if (!permissionTypeId) return; // Prevent running if no ID

            try {
                setIsEditLoading(true);
                const response = await axios.get(`${apiUrl}auth-service/permission-type/${permissionTypeId}`, {
                    headers: { 'Authorization': `Bearer ${bearerToken}` }
                });

                if (response.data.success && response.data.data) {
                    const permissionTypes = response.data.data;
                    newPermissionType.setValues({
                        name: permissionTypes.name || '',
                    });
                }
            } catch (error) {
                console.error('Error fetching permission type:', error.message);
            } finally {
                setIsEditLoading(false);
            }
        };

        if (open) {
            fetchPermissionType();
        }
    }, [permissionTypeId, open]);

    const onSubmit = async (values) => {
        setIsLoading(true);
        try {
            if (permissionTypeId) {
                await axios.put(`${apiUrl}auth-service/permission-type/${permissionTypeId}`, values, {
                    headers: {
                        'Authorization': `Bearer ${bearerToken}`,
                        'Content-Type': 'application/json',
                        'accept': 'application/json',
                    },
                });
                toast.success('Permission type updated successfully!');
            } else {
                await axios.post(`${apiUrl}auth-service/permission-type`, values, {
                    headers: {
                        'Authorization': `Bearer ${bearerToken}`,
                        'Content-Type': 'application/json',
                        'accept': 'application/json',
                    },
                });
                toast.success('Permission type created successfully!');
            }
        } catch (error) {
            console.error('Error inserting data:', error.message);
            toast.error('Something went wrong while saving.' || error.message);
        } finally {
            setIsLoading(false);
            setTimeout(() => {
                handleClose();
            }, 1000);
        }
    };

    const newPermissionType = useFormik({
        initialValues: {
            name: '',
        },
        validationSchema: Yup.object({
            name: Yup.string().required('Required'),
        }),
        onSubmit,
    });

    if (!open) return null;

    return (
        <>
            <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center p-4 z-50">
                {/* Background overlay */}
                <div className="absolute inset-0 bg-black opacity-30" onClick={onClose}></div>

                {/* Modal */}
                <div className="bg-white rounded-lg w-full max-w-xl z-50 relative">
                    {/* Header */}
                    <div className="bg-blue-500 text-white py-3 px-6 rounded-lg w-3/4 mx-auto relative z-10 -mt-6">
                        <h2 className="text-xl font-semibold text-center">Permission Type</h2>
                    </div>

                    <div className="relative bg-white p-6 space-y-6 shadow-md rounded-b-lg">
                        {isEditLoading ? (
                            <div className="space-y-6 animate-pulse">
                                <div className="flex flex-col sm:flex-row sm:items-end gap-4">
                                    {/* Skeleton for input */}
                                    <div className="flex-1">
                                        <div className="h-10 bg-gray-200 rounded-md w-full mb-2"></div>
                                        <div className="h-4 bg-gray-200 rounded w-1/2"></div>
                                    </div>
                                    {/* Skeleton for button */}
                                    <div className="h-10 w-24 bg-blue-200 rounded-md"></div>
                                </div>
                            </div>
                        ) : (
                            <form onSubmit={newPermissionType.handleSubmit} className="space-y-6">
                                <div className="flex flex-col sm:flex-row sm:items-end gap-4">
                                    <div className="flex-1">
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Permission Type Name
                                        </label>
                                        <input
                                            type="text"
                                            name="name"
                                            placeholder="Name"
                                            value={newPermissionType.values.name}
                                            onChange={newPermissionType.handleChange}
                                            onBlur={newPermissionType.handleBlur}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        />
                                        {newPermissionType.touched.name && newPermissionType.errors.name && (
                                            <div className="text-red-500 text-xs mt-1">{newPermissionType.errors.name}</div>
                                        )}
                                    </div>
                                    <button
                                        type="submit"
                                        disabled={isLoading}
                                        className="self-end px-6 py-2 h-10 bg-blue-500 text-white rounded-md hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors"
                                    >
                                        {isLoading ? 'Saving...' : 'Save'}
                                    </button>
                                </div>
                            </form>
                        )}

                        {isLoading && (
                            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                                <div className="bg-white p-4 rounded-lg flex flex-col items-center shadow-lg">
                                    <svg
                                        className="animate-spin h-8 w-8 text-blue-600 mb-3"
                                        xmlns="http://www.w3.org/2000/svg"
                                        fill="none"
                                        viewBox="0 0 24 24"
                                    >
                                        <circle
                                            className="opacity-25"
                                            cx="12"
                                            cy="12"
                                            r="10"
                                            stroke="currentColor"
                                            strokeWidth="4"
                                        ></circle>
                                        <path
                                            className="opacity-75"
                                            fill="currentColor"
                                            d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                                        ></path>
                                    </svg>

                                    <p className="text-sm font-medium text-gray-700">Saving...</p>
                                </div>
                            </div>
                        )}

                    </div>

                </div>

            </div>
            <ToastContainer
                position="bottom-right"
                autoClose={5000}
                hideProgressBar={false}
                newestOnTop={false}
                closeOnClick
                rtl={false}
                pauseOnFocusLoss
                draggable
                pauseOnHover
                theme="light"
            />
        </>
    );
}

PermissionTypeModel.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    permissionTypeId: PropTypes.string,
};